import { CreditRiskClassification } from "./companyMonitorTypes";
import type {DataForPayday} from "./dataForPaydayTypes";

export interface PaginationData {
  pageIndex: number
  pageSize: number
  totalItemCount: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

export enum SalaryInterval {
  monthly = 'monthly',
  biweekly = 'biweekly'
}

export enum CountryCode {
  se = 'se',
  no = 'no'
}

export enum Language {
  sv = 'sv',
  en = 'en'
}

export interface Money {
  amount: number,
  currencyCode: string
}

export type CreditFacilityRepaymentStatus =
  'due' | 'overdue' | 'excluded' | 'completed' | 'configurationError' | 'consistencyError' | 'upcoming'

export interface CreditFacilityRepaymentDashboardItem {
  creditFacilityPayoutId: string
  creditFacilityPayoutTransactionId: string
  payoutCreditFacilityId: string
  customerId: string
  customerName: string
  companyRegistrationNumber: string
  customerReferencePayoutId: string
  repaymentDate: string
  overdueDays: number
  amount: Money
  status: CreditFacilityRepaymentStatus
  ignoreWhenMatchingBankAccountTransactions: boolean
  matchedBankAccountTransactionId?: string
  matchedBankAccountTransactionDate?: string
  matchedBankAccountTransactionReference?: string
  repaymentTransactionId?: string
  repaymentTransactionDate?: string
  repaidDate?: string
}

export type IntegrationApiType = 'EmployerApi' | 'PayoutApi'

export interface Payday {
  id: string
  paymentDate: string
  completed: boolean
  locked: boolean
}

export enum CurrencyCodeString {
  sek = 'SEK',
  nok = 'NOK',
  dkk = 'DKK',
  eur = 'EUR'
}

export interface PayoutSettings {
  minAmountPerPayout?: number
  maxAmountPerPayout?: number
  maxAmountPerPayday?: number
  maxPercentPerPayday?: number
  maxNumberOfPayoutsPerPayday?: number
  currency?: CurrencyCodeString
  fee?: number
}

export type PayoutMethodType = 'SwishPayout' | 'BankTransfer'
export interface PayoutMethod {
  isDefault: boolean,
  payoutMethodType: PayoutMethodType,
  target: string
}

export interface PaydayListItemModel {
  id: string
  offCycle: boolean
  paymentDate: string
  employerId: string
  employerName: string
  hasValidationErrors: boolean
  payrollFileSubmitted: boolean
  completed: boolean
  highlighted: boolean
}

export interface PaydayModel {
  id: string
  offCycle: boolean
  paymentDate: string
  employerId: string
  employerName: string
  completed: boolean
  completedAt: string
  payrollDetails?: PayrollDetails
  employerPayRunDetails?: EmployerPayRunDetails
  employerDeductionDetails?: EmployerDeductionDetails
}
export type ValidationErrorType = 'UnknownEmployeeInPayroll' | 'EmployeeMissingInPayroll' | 'EmployeeWithNegativeBalance' | 'NonActivatedEmployeeInPayroll' | 'EmployeeWithIncorrectBankAccount'
export interface PayrollValidationError {
  parameters: string
  error: ValidationErrorType
}
export interface PayrollFile {
  id: string
  externalId: string
  submittedDate: string
  payCount: number
  originalPayCount: number
  rows: PayrollFileRow[]
}
export interface PayrollDetails {
  paymentDate: string
  validationErrors: PayrollValidationError[]
  payrollFiles: PayrollFile[]
}
export interface PayrollFileRow {
  employeeId: string
  employeeExternalId: string
  identificationNumber: string
  userId: string
  employeeName: string
  netAmount: Money
}
export type MoneyTransferState = 'Pending' | 'BusinessTransactionCommitPending' | 'Completed'
export type MoneyTransferType = 'DebtTransfer' | 'SalaryTransfer'
export interface BankTransferDetails {
  amount: Money
  state: MoneyTransferState
  type: MoneyTransferType
}
export type PayRunType = 'Default' | 'RemainingDebt'
export type PayRunState = 'Created' | 'ConnectedToBankAccountTransaction' | 'EmployerSalaryBusinessTransactionCommitPending' | 'EmployerSalaryBusinessTransactionCommitted' | 'TransferPending' | 'Completed'
export type DebtTransferState = 'None' | 'Pending' | 'Completed'
export type EmployerDeductionState = 'None' | 'Completed'

export enum PayTransferState {
  None = 'none',
  TransferPending = 'transferPending',
  TransferCompleted = 'transferCompleted',
  PayoutPending = 'payoutPending',
  Completed = 'completed',
  Paused = 'paused'
}

export enum PayRunPayoutAttemptState {
  None = 'none',
  Scheduled = 'scheduled',
  Pending = 'pending',
  Completed = 'completed',
  Failed = 'failed',
  FailedDueToInvalidSwishNumber = 'failedDueToInvalidSwishNumber'
}

export interface PayRunPayoutAttempt {
  initiatedAt: string
  scheduledAt: string | null
  completedAt: string | null
  state: PayRunPayoutAttemptState
  payoutMethod: PayoutMethod
  errorMessage: string | null
}
export interface PayRunModel {
  id: string
  created: string
  payDayId: string
  userId: string
  userGivenName: string
  userFamilyName: string
  userIdentificationNumber: string
  employerId: string
  employerName: string
  employeeId: string
  employeeExternalId: string
  payoutMethod: PayoutMethod
  originalSalary: Money
  deductedPayoutAmount: Money
  deductedFeeAmount: Money
  salaryAfterDeductions: Money
  deductedPayoutAmountByEmployer: Money
  type: PayRunType
  state: PayRunState
  payTransferState: PayTransferState
  debtTransferState: DebtTransferState
  employerDeductionState: EmployerDeductionState
  partial: boolean
  frozenDueToPendingPayouts: boolean
  frozenDueToSalaryDeficit: boolean
  frozenDueToMissingInPayrollFile: boolean
  payoutAttempts: PayRunPayoutAttempt[]
}
export interface PayRunListItemModel {
  id: string
  userGivenName: string
  userFamilyName: string
  originalSalary: Money
  salaryAfterDeductions: Money
  deductedAmountByEmployer: Money
  type: PayRunType
  state: PayRunState
  frozen: boolean
}

export interface EmployerPayRunDetails {
  bankTransfers: BankTransferDetails[]
}

export interface EmployerDeductionDetails {
  amount: Money
}

export interface EmployeeTaxSettingsModel {
  taxTableIdentifier?: string
  fixedTaxRatePercent?: number
  payoutTaxInPercent?: number
}

export type EmployeeStatus = 'registered' | 'connected' | 'active' | 'paused' | 'terminated'
export enum EmployeeType {
  worker = 'worker',
  salaried = 'salaried'
}
export enum PayCalculationType {
  default = 'default',
  shiftBasedSalary = 'shiftBasedSalary'
}
export interface EmployeeListItemModel {
  id?: string
  userId?: string
  externalId: string
  givenName?: string
  familyName?: string
  identificationNumber?: string,
  email?: string
  phoneNumber?: string
  employerName: string
  status: EmployeeStatus
}

export interface ConnectedUserModel {
  id: string
  givenName: string
  familyName: string
  connectedDate: string
}

export interface EmployeeModel {
  id?: string
  type: EmployeeType
  externalId?: string
  employmentNumber?: string
  givenName?: string
  familyName?: string
  identificationNumber?: string
  bankAccountNumber?: string
  email?: string
  phoneNumber?: string
  department?: string
  category?: string
  location?: string
  costCenter?: string
  payrollFileIdentifier?: string
  activated?: boolean
  activationDate?: string
  extendTerminationToUpcomingPayday: boolean
  paused?: boolean
  pauseReason?: string
  pauseDate?: string
  nonEligible: boolean
  underForeclosure: boolean
  pendingBankAccountActivation: boolean
  bankAccountActivationFailed: boolean
  taxSettings: EmployeeTaxSettingsModel
  status: EmployeeStatus
  payoutSettings: PayoutSettings
  effectivePayoutSettings: PayoutSettings
  connectedUser?: ConnectedUserModel
  employerId: string
  employerName: string
  displayPayoutLimitationInfo: boolean
  payoutLimitationReason?: string
  lastPayDataUpdate: string
  isEligibleForActivationReminder: boolean
  welcomeMessageSent: boolean
  lastActivationReminderDate?: string
  nextActivationReminderDate?: string
  employmentContracts: EmploymentContractModel[]
  openShifts: OpenShiftModel[]
  internalComment?: string
}

export interface OpenShiftModel {
  id: string
  externalId: string
  date: string
  hours: number
  isNew: boolean
  scheduleConflict: boolean
  restingTimeRuleViolation: boolean
  startTime?: string
  endTime?: string
  location1?: string
  location2?: string
  location3?: string
  comment?: string
  field1?: string
  field2?: string
  externalUrl?: string
  grossWages: Money | null
  estimatedGrossWage: Money | null
  estimatedNetWage: Money | null
}

export interface EmploymentContractModel {
  id: string
  externalId?: string
  reportId?: string
  description?: string
  collectiveAgreement?: string
  position?: string
  costCenter?: string
  monthlyWorkHours?: number
  weeklyWorkHours?: number
  employmentRatePercent?: number
  payScheduleId: string
  payScheduleIdentifier: string
  payCalculationType: PayCalculationType
  startDateString: string
  startDate: string
  endDateString?: string
  endDate?: string
  terminated: boolean
  handleVacationPay: boolean
  vacationPayPercent?: number
  currentSalary?: Money,
  currentHourlyWage?: Money,
  hourlyWage?: Money,
  monthlySalary?: Money,
  canonicalPays: CanonicalPayModel[],
  pays: PayModel[]
}

export interface CanonicalPayModel {
  grossHourlyWage: Money
  grossSalary: Money
  startDateString: string
  startDate: string
  endDateString?: string
  endDate?: string
}

export interface PayModel {
  id: string
  grossHourlyWage: Money
  grossSalary: Money
  startDateString: string
  startDate: string
  revision: number
}

export interface EmployerBankAccount {
  id: string
  bankAccountNumber: string
}

export interface PortalRoleModel {
  id: string
  name: string
  // Permissions
  allowManageUsers: boolean
  allowManagePaySchedules: boolean
  allowManagePayroll: boolean
  allowManageEmployees: boolean
  allowManageShifts: boolean
  allowManagePayDeviations: boolean
  allowManageEmployeeActivations: boolean
  allowManageCompanySettings: boolean
  allowManageBookkeepingSettings: boolean
  allowManageFora: boolean
  allowManageAgi: boolean
  allowViewIntegrationLog: boolean
  allowManageIntegrations: boolean
  // Reports and notifications
  allowReadCreditReport: boolean
  receiveAgiAdjustmentReminder: boolean
  receiveCreditReport: boolean
  receiveActivationNotification: boolean
  receivePayrollSystemImportFileNotification: boolean
  receiveDeductionRepaymentNotification: boolean
  receivePayrollFileReminder: boolean
  receivePaydayGenerationNotification: boolean
  receivePayrollFileValidationErrorNotification: boolean
}

export interface CreateOrUpdatePortalRoleModel {
  name: string
  // Permissions
  allowManageUsers: boolean
  allowManagePaySchedules: boolean
  allowManagePayroll: boolean
  allowManageEmployees: boolean
  allowManageShifts: boolean
  allowManagePayDeviations: boolean
  allowManageEmployeeActivations: boolean
  allowManageCompanySettings: boolean
  allowReadCreditReport: boolean
  allowManageBookkeepingSettings: boolean
  allowManageFora: boolean
  allowManageAgi: boolean
  allowViewIntegrationLog: boolean
  allowManageIntegrations: boolean
  // Reports and notifications
  receiveAgiAdjustmentReminder: boolean
  receiveCreditReport: boolean
  receiveActivationNotification: boolean
  receivePayrollSystemImportFileNotification: boolean
  receiveDeductionRepaymentNotification: boolean
  receivePayrollFileReminder: boolean
  receivePaydayGenerationNotification: boolean
  receivePayrollFileValidationErrorNotification: boolean
}

export interface CustomerPortalUser {
  portalUserId: string
  identificationNumber: string
  authScheme: string
  givenName: string
  familyName: string
  email: string
  phoneNumber: string
  language: Language
  customerId: string
  customerName: string
  portalRoles: PortalRoleModel[]
  isOwner: boolean
  isContactTechnical: boolean
  isContactPayroll: boolean
  isContactFinance: boolean
}

export interface UpdateCustomerPortalUserModel {
  identificationNumber: string
  email: string
  portalRoleIds: string[]
  isOwner: boolean
  isContactTechnical: boolean
  isContactPayroll: boolean
  isContactFinance: boolean
}

export enum Periodicity {
  monthly = 'monthly'
}

export enum AccrualType {
  arrears = 'arrears',
  advance = 'advance'
}

export enum PaydayOnWeekendHandling {
  advance = 'advance',
  postpone = 'postpone'
}

export interface PayScheduleEditModel {
  id: string | null
  identifier: string
  periodicity: Periodicity
  salaryAccrualType: AccrualType
  salaryEarningOnWeekends: boolean
  periodStartDayOfMonth: number
  deviationPeriodEndOffset: number
  defaultPaymentDayOfMonth: number
  paydayOnWeekendHandling: PaydayOnWeekendHandling
  enableAdvanceShifts: boolean
}

export interface PayScheduleListItem extends PayScheduleEditModel {
  isDefault: boolean
}

export interface PaySchedulePaydayModel {
  id: string
  paymentDate: string
  isShared: boolean
  isReadOnly: boolean
  payoutsDisabled: boolean
  payrollExists: boolean
}

export interface PayScheduleModel extends PayScheduleListItem {
  paydays: PaySchedulePaydayModel[]
}

export interface CreditorModel {
  id: string
  name: string
}

export interface EmployerNameModel {
  id: string
  name: string
}

export interface CustomerListEmployerInfoModel {
  id: string,
  state: EmployerState
}
export interface CustomerListPayoutCreditFacilityInfoModel {
  id: string,
  state: PayoutCreditFacilityState
}
export interface CustomerListItemModel {
  id: string
  marketId: string
  companyRegistrationNumber?: string
  name?: string
  legalName?: string
  country: CountryCode
  state: CustomerState
  employer: CustomerListEmployerInfoModel | null,
  payoutCreditFacility: CustomerListPayoutCreditFacilityInfoModel | null
}
export interface EmployerListItemModel {
  id: string
  companyRegistrationNumber?: string
  name?: string
  state: CustomerState
}
export interface PayoutCreditFacilityListItemModel {
  id: string
  companyRegistrationNumber?: string
  name?: string
  marketId: string
  state: PayoutCreditFacilityState
}
export interface CreditFacilityPayoutListItemModel {
  id: string
  payoutIntentId: string
  customerReferenceIntentId: string
  customerReferencePayoutId: string
  userId: string
  recipientIdentificationNumber: string
  amount: Money
  repaymentDate: string
  payoutMethodType: PayoutMethodType
  payoutMethodTarget: string
  state: CreditFacilityPayoutState
  failureReason: string | null
  created: string
  completedDate: string | null
}
export type CustomerState = 'active' | 'locked' | 'terminated'
export type EmployerState = 'active' | 'locked' | 'terminated'
export type PayoutCreditFacilityState = 'active' | 'locked' | 'terminated'
export type CreditFacilityPayoutState = 'signingPending' | 'reservationPending' | 'transferFundsToUserPending' | 'completed' | 'failed'
export enum EmployeeActivationType {
  bankAccount,
  manual,
  automatic
}
export enum EmployeeDeactivationType {
  manual,
  automatic
}
export enum PayrollSystem {
  unknown,
  caspeco,
  personalkollen,
  fortnox,
  flexHRM,
  vismaHrPlus,
  visma600,
  kontek,
  crona,
  hogia,
  agda,
  spiris,
  sdWorx
}
export enum PayrollFileFormat {
  none,
  bankgiro,
  hogia,
  fortnoxPdf,
  sus,
  painV03,
  agda,
  susPainV03Split
}
export enum PayrollSystemImportFileFormat {
  paXml,
  tlu,
  hogia,
  csv,
  vismaHrPlus,
  multiRowCsv
}
export enum EmployeeDataParserType {
  csv,
  hogia,
  fram,
  visma600UpdateOnly,
  visma600CreateAndUpdate,
  agdaXlsx
}
export enum AdminFlowType {
  basisImport,
  agiAdjustment
}

export enum CalendarMonthPayoutBasisReportGenerationType {
  automatic,
  manual
}
export enum PayrollFileEmployeeIdentifierType {
  id10,
  externalId,
  custom,
  id12
}
export enum PortalUserAuthenticationScheme {
  password,
  cappy
}
export interface CreditorConnectionModel {
  id: string
  name: string
  priority: number
}
export interface CompanyReportModel {
  created: string,
  rawReport: string
}

export interface CustomerModel {
  id: string
  companyRegistrationNumber?: string
  name?: string
  legalName?: string
  industry?: string
  agreementNumber?: string
  marketId: string
  marketName: string
  language: Language
  state: CustomerState
  timeZoneId: string
  employer: CustomerListEmployerInfoModel | null,
  payoutCreditFacility: CustomerListPayoutCreditFacilityInfoModel | null
  invoiceEmailAddress: string | null
  bankName: string
  bankAccountType: BankTransferType | null
  bankAccountNumber: string
  bic: string
  defaultPortalUserAuthenticationScheme: PortalUserAuthenticationScheme
  portalUsers: CustomerPortalUser[]
  portalRoles: PortalRoleModel[]
}
export interface PayoutCreditFacilityModel {
  id: string
  customerId: string
  customerName: string
  repaymentBankAccountId: string | null
  marketId: string
  state: PayoutCreditFacilityState
  currency: CurrencyCodeString
  launchDate: string | null
  creditLimit: Money | null
  creditUsage: Money
  remainingCreditSpaceWarningPercent: number,
  creditSpaceWarningSilencedUntilDate: string | null,
  creditors: CreditorConnectionModel[]
  minimumPayoutAmount: number | null
  maximumPayoutAmount: number | null
  minimumDaysUntilRepaymentDate: number | null
  maximumDaysUntilRepaymentDate: number | null
  maxCreditPerUser: number | null
  allowedOrigins: string[]
}
export enum BankTransferType {
   domesticBbanTransfer,
   crossBorderIbanTransfer,
   domesticBankgiroTransfer,
   domesticPlusgiroTransfer
}
export type CreditFacilityPayoutModel = CreditFacilityPayoutListItemModel

export enum EmployeeBankAccountRegisterType {
  internal,
  external
}

export interface PayrollSystemConfigurationModel {
  employerId: string
  adminFlowType: AdminFlowType
  employeeBankAccountRegisterType: EmployeeBankAccountRegisterType
  payrollSystem: PayrollSystem
  payrollFileFormat: PayrollFileFormat
  payrollFileEmployeeIdentifierType: PayrollFileEmployeeIdentifierType
  enablePayrollFileAdjustment: boolean
  manualPayrollFileRowCreationEnabled: boolean
  payrollFileRowDeletionEnabled: boolean
  payrollFileExecutionDateOneDayBeforePayment: boolean
  payrollFileHandlerConfiguration: string | null
  payrollSystemImportFileFormat: PayrollSystemImportFileFormat
  calendarMonthPayoutBasisReportGenerationType: CalendarMonthPayoutBasisReportGenerationType
  calenderMonthPayoutBasisReportGenerationDayOfMonth: number | null
  disableDeductionFileGeneration: boolean
  enableBankAccountImportFile: boolean
  enableEmployeeDeactivationImportFile: boolean
  bankAccountImportFileCustomField: string | null
  payrollCodes: PayrollCodeConfigurationModel[]
  automaticPayrollCreationDaysBeforePayday: number | null
  payrollCreationDisabledUntilDaysBeforePayday: number | null
  useParallelEmploymentContracts: boolean
}

export enum PayArticleValueSign {
  positive = 'positive',
  negative = 'negative'
}

export interface CreatePayArticleTypeModel {
  code: string
  displayName: string
  isDefaultSalary: boolean
  isDefaultStandardShift: boolean
  isDefaultCustomShift: boolean
  isDefaultPayDeviation: boolean
  availabilityRatio: number
  isShiftBasedSalaryArticle: boolean
  valueSign: PayArticleValueSign
  accrualType: AccrualType
}

export interface UpdatePayArticleTypeModel {
  displayName: string
  isDefaultSalary: boolean
  isDefaultStandardShift: boolean
  isDefaultCustomShift: boolean
  isDefaultPayDeviation: boolean
  availabilityRatio: number
  isShiftBasedSalaryArticle: boolean
  valueSign: PayArticleValueSign
  accrualType: AccrualType
}

export interface PayArticleTypeModel {
  id: string
  employerId: string
  code: string
  displayName: string
  isDefaultSalary: boolean
  isDefaultStandardShift: boolean
  isDefaultCustomShift: boolean
  isDefaultPayDeviation: boolean
  availabilityRatio: number
  isShiftBasedSalaryArticle: boolean
  valueSign: PayArticleValueSign
  accrualType: AccrualType
}

export enum PayrollCodeType {
  advance = 'advance',
  deduction = 'deduction'
}

export enum PayrollCodeValueSource {
  value = 'value',
  netAmount = 'netAmount',
  grossAmount = 'grossAmount',
  taxAmount = 'taxAmount',
  taxPercentage = 'taxPercentage',
  vacationPay = 'vacationPay'
}

export interface PayrollCodeConfigurationModel {
  id: string | null
  type: PayrollCodeType
  code: string
  valueSource: PayrollCodeValueSource
  value: string | null
  negate: boolean
  includeIdentificationNumber: boolean
  includeName: boolean
  includeFromDate: boolean
  includeToDate: boolean
}

export interface EmployerModel {
  id: string
  customerId: string
  customerName: string
  marketId: string
  appAbbreviation?: string
  payoutSettings: PayoutSettings
  effectivePayoutSettings: PayoutSettings
  payrollSystemConfiguration: PayrollSystemConfigurationModel
  state: EmployerState
  bankAccount?: EmployerBankAccount
  launchDate: string
  employeeDataParserType: EmployeeDataParserType
  employeeActivationType: EmployeeActivationType
  employeeDeactivationType: EmployeeDeactivationType
  activationNotificationDaysOfWeek: string[]
  approvedCreditLimit: Money
  availableCreditLimit: Money
  creditUsage: Money
  remainingCreditSpaceWarningPercent: number,
  creditSpaceWarningSilencedUntilDate: string,
  lastShiftOrSalaryUpdate: string
  defaultVacationPayPercent: number | null
  defaultPayoutTaxInPercent: number
  defaultTaxTableIdentifier: string
  defaultFixedTaxRatePercent: number | null
  payoutTaxImmutable: boolean
  paySchedules: PayScheduleListItem[]
  creditors: CreditorConnectionModel[]
  newEmployeeMessageEnabled: boolean
  employeeActivationReminderEnabled: boolean
  employeeActivationReminderIntervalDays: number
  plannedShiftsEnabled: boolean
  openShiftsEnabled: boolean
  newEmployeesReportEnabled: boolean
  bookkeepingConfiguration?: BookkeepingConfigurationModel
  employerLogos: EmployerLogoModel[]
  freePayoutCountOnEmployeeActivation: number
  employerInitiatedPayoutsEnabled: boolean
  employerInitiatedPayoutTaxPercent: number
  employerInitiatedPayoutClientConfiguration: string
  employeeMinAgeInYears: number
  shiftSyncMaxDeletePercentage: number
  shiftOrDeviationStaleWarningThreshold: string
  wfmSystem: string
  hrSystem: string
  payrollAgency: string
}

export interface EmployerLogoModel
{
  theme: string,
  contentType: string,
  image: string
}

export interface CreateFreePayoutModel {
  numberOfFreePayouts: number
}

export interface CreateNewCustomerModel {
  companyRegistrationNumber: string
  name: string
  legalName: string
  marketId: string
  language: Language,
}
export interface CreateNewEmployerModel {
  customerId: string
}
export interface CreateNewPayoutCreditFacilityModel {
  customerId: string
  currency: CurrencyCodeString
  allowedOrigins: string[]
}

export interface UpdateCustomerModel {
  companyRegistrationNumber?: string
  name?: string
  legalName?: string
  industry?: string
  agreementNumber?: string
  language: Language
  timeZoneId: string
  invoiceEmailAddress: string | null
  bankName: string
  bankAccountType: BankTransferType | null
  bankAccountNumber: string
  bic: string
  defaultPortalUserAuthenticationScheme: PortalUserAuthenticationScheme
}

export interface UpdateEmployerModel {
  appAbbreviation?: string
  approvedCreditLimit: number
  availableCreditLimit: number
  remainingCreditSpaceWarningPercent: number,
  employeeActivationType: EmployeeActivationType
  employeeDeactivationType: EmployeeDeactivationType
  employeeDataParserType: EmployeeDataParserType
  defaultVacationPayPercent: number | null
  defaultPayoutTaxInPercent: number
  defaultTaxTableIdentifier: string
  defaultFixedTaxRatePercent: number | null
  payoutTaxImmutable: boolean
  launchDate: string
  newEmployeeMessageEnabled: boolean
  employeeActivationReminderEnabled: boolean
  employeeActivationReminderIntervalDays: number
  plannedShiftsEnabled: boolean
  openShiftsEnabled: boolean
  newEmployeesReportEnabled: boolean
  freePayoutCountOnEmployeeActivation: number
  employerInitiatedPayoutsEnabled: boolean
  employerInitiatedPayoutTaxPercent: number
  employerInitiatedPayoutClientConfiguration: string
  employeeMinAgeInYears: number
  shiftSyncMaxDeletePercentage: number
  shiftOrDeviationStaleWarningThreshold: string
  wfmSystem: string
  hrSystem: string
  payrollAgency: string
}

export interface UpdatePayoutCreditFacilityModel {
  creditLimit: number
  remainingCreditSpaceWarningPercent: number,
  launchDate: string | null
  currency: CurrencyCodeString
  minimumPayoutAmount: number | null
  maximumPayoutAmount: number | null
  minimumDaysUntilRepaymentDate: number | null
  maximumDaysUntilRepaymentDate: number | null
  maxCreditPerUser: number | null
  allowedOrigins: string[]
}

export interface IntegrationSigningCredentialModel {
  id: string
  activeFrom: string
  expiresAt: string | null
  revokedAt: string | null
  thumbprint: string | null
}

export interface CreateIntegrationSigningCredentialModel {
  publicCertificate: string
  activeFrom: string
  expiresAt: string | null
}

export interface ConnectedEmployeeModel {
  id: string
  externalId: string
  activationDate: string
  connectedToUserDate: string
  status: EmployeeStatus
  employerId: string
  employerName: string
}

export interface EmployerPaydayDataModel {
  employerId: string
  employerName: string
  payoutSettings: PayoutSettings
  paydays: DataForPayday[]
}

export interface UserFreePayouts {
  available: number
  used: number
}

export interface UserPayoutCooldownPeriodModel
{
  start: string
  end: string
  state: string
  isActive: boolean
}

export enum ComplianceStatus {
  unknown = 'unknown',
  compliant = 'compliant',
  nonCompliant = 'nonCompliant'
}

export interface UserModel {
  id: string
  givenName: string
  familyName: string
  birthDate?: string
  identificationNumber?: string
  email: string
  mobilePhone: string
  unverifiedMobilePhone: string
  marketId: string
  marketName: string
  preferredLanguage: Language
  payoutSettings: PayoutSettings
  payoutMethods: PayoutMethod[]
  state: string
  accountDeletionDate: string
  accountDeletionRequestDate: string
  createdDate: string
  latestLoginDate?: string
  numberOfLogins: number
  appVersion: string
  activationGuideCompleted: boolean
  dataCollectionConsent: boolean
  internalComment: string
  connectedEmployees: ConnectedEmployeeModel[]
  employerPaydayData: EmployerPaydayDataModel[]
  freePayouts: UserFreePayouts
  mobilePhoneVerificationDisabled: boolean
  appServicesEnabled: boolean
  payoutCooldownPeriods: UserPayoutCooldownPeriodModel[]
  complianceStatus: ComplianceStatus
}

export interface UserListItemModel {
  id: string
  givenName: string
  familyName: string
  identificationNumber?: string
  marketId: string
  state: string
  complianceStatus: ComplianceStatus
}

export interface PagedListItems<T> {
  items: T[]
  totalCount: number
}

export enum TransactionStatus {
  Pending = 'pending',
  Cancelled = 'cancelled',
  Completed = 'completed'
}
export enum TransactionType {
  CreditorPrefundingTransaction = 'CreditorPrefundingTransaction',
  EmployeePayoutTransaction = 'EmployeePayoutTransaction',
  EmployerPayPaymentTransaction = 'EmployerPayPaymentTransaction',
  TransferDebtsToLandingAccountTransaction = 'TransferDebtsToLandingAccountTransaction',
  TransferPaysToOperatingAccountTransaction = 'TransferPaysToOperatingAccountTransaction',
  PayPayoutTransaction = 'PayPayoutTransaction',
  CreditorDebtSettlementTransaction = 'CreditorDebtSettlementTransaction',
  CreditorDebtRecycleTransaction = 'CreditorDebtRecycleTransaction',
  TransferFeesToTransactionAccountTransaction = 'TransferFeesToTransactionAccountTransaction',
  EmployerPayDeductionPaymentTransaction = 'EmployerPayDeductionPaymentTransaction',
  CreditorPostfundingTransaction = 'CreditorPostfundingTransaction',
  FundingForPostfundingCreditorsTransaction = 'FundingForPostfundingCreditorsTransaction',
  FundingForPostfundingCreditorsRepaymentTransaction = 'FundingForPostfundingCreditorsRepaymentTransaction',
  CreditFacilityPayoutTransaction = 'CreditFacilityPayoutTransaction',
  CreditFacilityPayoutRepaymentTransaction = 'CreditFacilityPayoutRepaymentTransaction',
  TransferCreditFacilityPayoutRepaymentsToLandingAccountTransaction = 'TransferCreditFacilityPayoutRepaymentsToLandingAccountTransaction',
}
export interface TransactionListItemModel {
  id: string
  date: string
  type: TransactionType
  state: TransactionStatus
  amount: Money
  employerId?: string
  payoutCreditFacilityId?: string
  customerName?: string
  userId?: string
  userGivenName?: string
  userFamilyName?: string
  paydayDate?: string
}

export interface EmployeePayoutTransactionAdditions {
  fee: Money
  payoutMethod: PayoutMethod
  repaymentDate: string
  employerId: string
  employerName: string
  userId: string
  userGivenName: string
  userFamilyName: string
  failureReason: string
}

export interface EmployerPayPaymentTransactionAdditions {
  deductedFeeAmount: Money
  deductedPayoutAmount: Money
  remainingPayAmount: Money
  employerId: string
  employerName: string
  userId: string
  userGivenName: string
  userFamilyName: string
}

export interface PayPayoutTransactionAdditions {
  payoutMethod: PayoutMethod
  employerId: string
  employerName: string
  userId: string
  userGivenName: string
  userFamilyName: string
}

export interface TransferDebtsToLandingAccountTransactionAdditions {
  employerId: string
  employerName: string
}

export interface TransferPaysToOperatingAccountTransactionAdditions {
  employerId: string
  employerName: string
}

export interface EmployerPayDeductionPaymentTransactionAdditions {
  employerId: string
  employerName: string
}

export interface CreditorPostfundingTransactionAdditions {
  creditorId: string
  creditorName: string
  fundingCost: Money
}

export type CreditorFundingType = 'prefunding' | 'postfunding'

export interface CreditFacilityPayoutTransactionAdditions {
  creditFacilityPayoutId: string
  customerReferenceIntentId: string
  customerReferencePayoutId: string
  payoutCreditFacilityId: string
  customerName: string
  creditorId: string
  creditorName: string
  creditorFundingType: CreditorFundingType
  userId: string
  userGivenName: string
  userFamilyName: string
  repaymentDate: string
  payoutMethod: PayoutMethod
}

export interface CreditFacilityPayoutRepaymentTransactionAdditions {
  creditFacilityPayoutTransactionId: string
  customerReferenceIntentId: string
  customerReferencePayoutId: string
  payoutCreditFacilityId: string
  customerName: string
  creditorId: string
  creditorName: string
  creditorFundingType: CreditorFundingType
  userId: string
  userGivenName: string
  userFamilyName: string
  repaymentBankAccountId: string
  bankAccountTransactionId: string
}

export interface TransferCreditFacilityPayoutRepaymentsToLandingAccountTransactionAdditions {
  payoutCreditFacilityId: string
  customerName: string
  sourceBankAccountId: string
  bankTransferId: string
  bankTransferIdentifier: string
}

export interface TransactionModel {
  id: string
  date: string
  type: TransactionType
  state: TransactionStatus
  amount: Money
  employeePayoutTransactionDetails?: EmployeePayoutTransactionAdditions
  employerPayPaymentTransactionDetails?: EmployerPayPaymentTransactionAdditions
  payPayoutTransactionDetails?: PayPayoutTransactionAdditions
  transferDebtsToLandingAccountTransactionDetails?: TransferDebtsToLandingAccountTransactionAdditions
  transferPaysToOperatingAccountTransactionDetails?: TransferPaysToOperatingAccountTransactionAdditions
  employerPayDeductionPaymentTransactionDetails?: EmployerPayDeductionPaymentTransactionAdditions
  creditorPostfundingTransactionDetails?: CreditorPostfundingTransactionAdditions
  creditFacilityPayoutTransactionDetails?: CreditFacilityPayoutTransactionAdditions
  creditFacilityPayoutRepaymentTransactionDetails?: CreditFacilityPayoutRepaymentTransactionAdditions
  transferCreditFacilityPayoutRepaymentsToLandingAccountTransactionDetails?: TransferCreditFacilityPayoutRepaymentsToLandingAccountTransactionAdditions
}

export interface ReconciliationListItemModel {
  id: string
  date: string
  hasUnhandledIssues: boolean
  totalIssueCount: number
}

export interface ReconciliationIssueModel {
  id: string
  reconciliationId: string
  details: string
  handled: boolean
  handledDate: string
  handledComment: string
}

export interface ReconciliationModel {
  id: string
  date: string
  hasUnhandledIssues: boolean
  issues: ReconciliationIssueModel[]
}

export interface BankAccountModel {
  marketId: string
  bankAccountId: string
  name: string
  bban: string
  connectedEmployers: EmployerConnectedToBankAccount[]
}

export interface EmployerConnectedToBankAccount {
  employerId: string
  employerName: string
}

export interface ShiftModel {
  id: string
  externalId: string
  date: string
  type: string
  status: string
  startTime: string | null
  endTime: string | null
  hours: number
  grossValue: Money
  description: string
  location: string
  employmentContract: EmploymentContractStub
  employee: EmployeeStub
  payArticles: PayArticleModel[]
}

export interface PayDeviationModel {
  id: string
  externalId: string
  date: string
  description: string
  grossValue: Money
  employmentContract: EmploymentContractStub
  employee: EmployeeStub
  payday: PaydayStub
  manuallyCreated: boolean
  payArticles: PayArticleModel[]
}

export interface PayArticleModel {
  code: string
  displayName: string
  isShiftBasedSalaryArticle: boolean
  grossValue: Money
  quantity: number
  accrualType: AccrualType
  payday: PaydayStub
  approved: boolean
}

export interface EmploymentContractStub {
  id: string
  externalId: string
  startDate: string
  endDate: string | null
}

export interface EmployeeStub {
  id: string
  externalId: string
  employmentNumber: string | null
  name: string
  identificationNumber: string
}

export interface PaydayStub {
  id: string
  paymentDate: string
}

export interface TokenModel {
  key: string
  description: string
  expires: string
}

export enum BankTransferState {
  PaymentVerificationRequired = 'paymentVerificationRequired',
  SecondarySigningRequired = 'secondarySigningRequired'
}
export interface FrozenBankTransfer {
  identifier: string
  amount: Money
  fromAccountBban: string
  toAccountBban: string
  state: BankTransferState
}

export interface MarketModel {
  id: string
  country: CountryCode
  payoutSettings: PayoutSettings
}

export interface TransactionFilterParams {
  userId: string | null
  userSearchTerm: string | null
  employerId: string | null
  employerSearchTerm: string | null
  creditFacilityId: string | null
  creditFacilitySearchTerm: string | null
  state: TransactionStatus | null
  transactionType: TransactionType | null
  fromDate: string | null
  toDate: string | null
  paydayDate: string | null
}

export interface EmployeeFilterParams {
  searchTerm: string | null
  employerId: string | null
}

export interface CreditFacilityPayoutFilterParams {
  searchTerm: string | null
  payoutCreditFacilityId: string | null
}

export interface EmployeeAndPaydayFilterParams {
  employeeId: string
  paydayId: string | null
}

export interface PaydayFilterParams {
  employerName: string | null
  employerId: string | null
  includeCompletedPaydays: boolean
}

export interface PayRunFilterParams {
  paydayId: string
  userId: string | null
  userName: string | null
  includeCompletedPayRuns: boolean
}

export interface ProtectCustomerModel {
  id: string
  employerId: string
  payoutCreditFacilityId: string
  name: string
  issues: ProtectIssue[]
  rules: ProtectRule[]
}

export interface ProtectCustomerListItem {
  id: string
  name: string
}

export enum ProtectIssueDecisionType {
  none = 'none',
  dismissed = 'dismissed',
  fipoReport = 'fipoReport',
  other = 'other'
}

export interface ProtectIssue {
  id: string
  ruleId: string
  ruleName: string
  isGlobalRule: boolean
  domain: string
  createdDate: string
  customerId: string
  customerName: string
  description: string
  investigationResult: string
  investigationDone: boolean
  investigationDate: string
  isSuspectedMlOrTf: boolean
  isSuspectedFraud: boolean
  decision: ProtectIssueDecisionType
  fipoReportDate: string
  actions: string
  closed: boolean
  closedDate: string | null
}

export interface ProtectIssueFilterParams {
  includeHandled: boolean
}

export interface ProtectRuleListItem {
  id: string
  name: string
  type: string
  customerId: string
  customerName: string
  triggered: boolean
}

export enum ProtectRuleType {
  PayoutVolumeZScoreRule = 'PayoutVolumeZScoreRule',
  ActivationRateZScoreRule = 'ActivationRateZScoreRule',
  OverdrawsRule = 'OverdrawsRule',
  AbnormalEarningsRule = 'AbnormalEarningsRule',
  PayoutVolumeRateRule = 'PayoutVolumeRateRule',
  AveragePayoutAmountRule = 'AveragePayoutAmountRule',
  MaxSalaryRule = 'MaxSalaryRule',
  MaxInvoicePayoutAmountRule = 'MaxInvoicePayoutAmountRule',
  InvoicePayoutRateRule = 'InvoicePayoutRateRule',
  InvoicePayoutVolumeRateRule = 'InvoicePayoutVolumeRateRule',
  InvoicePayoutRateForRecipientRule = 'InvoicePayoutRateForRecipientRule',
  InvoicePayoutVolumeRateForRecipientRule = 'InvoicePayoutVolumeRateForRecipientRule',
  InvoicePayoutVolumeZScoreRule = 'InvoicePayoutVolumeZScoreRule',
  PayoutVolumeThreeSigmaRule = 'PayoutVolumeThreeSigmaRule'
}

export interface ProtectRule {
  id: string
  name: string
  type: ProtectRuleType
  domain: string
  hysteresis: string
  customerId: string
  customerName: string
  triggered: boolean
  deleted: boolean
  rawResult: string

  triggerValue?: number
  currentWindowSize?: string
  compareWindowSize?: string
  skipDays?: number
  withdrawalLimit?: number
  minSampleThreshold?: number
  minActiveDays?: number
  relativeLift?: number
  activeMinThreshold?: number
}

export interface CreateOrUpdateProtectRuleModel {
  id?: string
  name: string
  type: ProtectRuleType | null
  hysteresis: string
  customerId: string

  triggerValue?: number
  currentWindowSize?: string
  compareWindowSize?: string
  skipDays?: number
  withdrawalLimit?: number
  minSampleThreshold?: number
  minActiveDays?: number
  relativeLift?: number
  activeMinThreshold?: number
}

export interface UpdateProtectIssueInvestigationModel {
  customerId?: string
  investigationResult?: string
  investigationDone: boolean
  isSuspectedMlOrTf: boolean
  isSuspectedFraud: boolean
}

export interface UpdateProtectIssueDecisionModel {
  issueClosed: boolean
  decision: ProtectIssueDecisionType
  fipoReportDate: string
  actions: string
}

export interface ProtectDailyZScoreResult {
  date: string
  value: number,
  zScore: number
}

export interface ProtectStandardDeviation {
  count: number,
  mean: number,
  variance: number,
  sigma: number,
  standardErrorMean: number
}

export interface ProtectZScoreResult {
  standardDeviation: ProtectStandardDeviation
  currentResult: ProtectDailyZScoreResult
  historicalResults: ProtectDailyZScoreResult[]
}

export interface ProtectUserAmount {
  amount: number
  userId: string
}

export interface ProtectRepaymentDateResult {
  description: string
  overdraws: ProtectUserAmount[]
  repaymentDate: string
}

export interface ProtectPayoutSizeResult {
  repaymentDateResults: ProtectRepaymentDateResult[]
}

export interface ProtectAbnormalEarnings {
  amount: Money
  employeeId: string
}

export interface ProtectAbnormalEarningsResult {
  abnormalEarnings: ProtectAbnormalEarnings[]
}

export interface ProtectPayoutVolumeRateResult {
  currentVolume: number
}

export interface ProtectAveragePayoutAmountResult {
  currentAverageAmount: number
}

export interface ProtectEmployeeWithSalary {
  employeeId: string
  grossSalary: Money
}

export interface ProtectMaxSalaryResult {
  salaries: ProtectEmployeeWithSalary[]
}

export interface ProtectInvoicePayoutWithAmount {
  invoicePayoutId: string
  amount: Money
}

export interface ProtectMaxInvoicePayoutAmountResult {
  invoicePayouts: ProtectInvoicePayoutWithAmount[]
}

export interface ProtectInvoicePayoutRateResult {
  currentCount: number
}

export interface ProtectInvoicePayoutVolumeRateResult {
  currentVolume: number
}

export interface ProtectInvoicePayoutRateForRecipient {
  recipientAccount: string
  count: number
}

export interface ProtectInvoicePayoutRateForRecipientResult {
  stats: ProtectInvoicePayoutRateForRecipient[]
}

export interface InvoicePayoutVolumeRateForRecipient {
  recipientAccount: string
  volume: number
}

export interface ProtectInvoicePayoutVolumeRateForRecipientResult {
  stats: InvoicePayoutVolumeRateForRecipient[]
}

export interface DashboardMetrics {
  cappyKpiMetrics: CappyKpiMetrics
  activityKpiMetrics : ActivityKpiMetrics
}

export interface TemporalValue<T>
{
  value: T
  date: string
}

export interface NumericTimeSeries {
  values: TemporalValue<number>[]
  firstValue: number
  lastValue: number
  min: number
  max: number
  delta: number
  average: number
  deltaPercentage: number
  currentPercentage: number
}

export interface MonetaryTimeSeries {
  values: TemporalValue<Money>[]
  firstValue: Money
  lastValue: Money
  min: Money
  max: Money
  delta: Money
  average: Money
  deltaPercentage: number
}

export interface CappyKpiMetrics {
  activatedEmployeeCount: NumericTimeSeries
  terminatedEmployeeCount: NumericTimeSeries
  nonEligibleEmployeeCount: NumericTimeSeries
  activatedTerminatedEmployeeCount: NumericTimeSeries
  activatedNonTerminatedEmployeeCount: NumericTimeSeries
  employeeCount: NumericTimeSeries
  eligibleEmployeeCount: NumericTimeSeries
  userCount: NumericTimeSeries
  deletedUserCount: NumericTimeSeries
  payoutCountSek: NumericTimeSeries
  payoutSumSek: MonetaryTimeSeries
  payPaymentCountSek: NumericTimeSeries
  payPaymentSumSek: MonetaryTimeSeries
  employeeActivationConversion: NumericTimeSeries
  creditUsageSek: MonetaryTimeSeries
  outstandingFeesSek: MonetaryTimeSeries
}

export interface ActivityKpiMetrics {
  dailyActiveUsers: NumericTimeSeries
  monthlyActiveUsers: NumericTimeSeries
  dailyActiveUsersWithdrawalShare: NumericTimeSeries
  monthlyActiveUsersWithdrawalShare: NumericTimeSeries
  productStickiness: NumericTimeSeries
}

export enum BookkeepingAccountType {
  salaryAccount = 'salaryAccount',
  taxAccount = 'taxAccount',
  cappyAdjustmentAccount = 'cappyAdjustmentAccount',
  socialFeeAccount = 'socialFeeAccount',
  socialFeeSettlementAccount = 'socialFeeSettlementAccount'
}

export interface BookkeepingConfigurationModel
{
  verificationDescription: string
  verificationsPerIndividual: boolean
  accountTypeConfigurations: BookkeepingAccountTypeConfigurationModel[]
}

export interface BookkeepingAccountTypeConfigurationModel
{
  accountType: BookkeepingAccountType
  includeCostCenterDimension: boolean
  includeEmploymentDimension: boolean
  includeProjectDimension: boolean
  rules: BookkeepingAccountTypeRuleModel[]
}

export interface BookkeepingAccountTypeRuleModel
{
  orderNumber: number
  account: number
  costCenter: string
  project: string
  filterEmployeeType: EmployeeType | undefined
  filterDepartment: string
  filterCategory: string
}

export interface PayoutSystemStatusModel
{
  severity: PayoutSystemStatusSeverity
  isBankSpecific: boolean
  banks: string[]
}

export enum PayoutSystemStatusSeverity {
  ok = 'ok',
  minor = 'minor',
  major = 'major',
  critical = 'critical'
}

////////////////////////////
// Learning management types
////////////////////////////
export interface ImageModel {
  name: string
  uri: string
}

export interface MultilingualString {
  [key: string]: string
}

export interface LearningCategoryManagementModel {
  id: string
  orderNumber: number
  color: string
  backgroundColor: string
  icon: string
  title: MultilingualString
  description: MultilingualString
  tags: string[]
}

export interface LearningCourseManagementModel {
  id: string
  learningCategoryId: string
  employerId: string | null
  isPublished: boolean
  title: MultilingualString
  description: MultilingualString
  imageUrl: string
  orderNumber: number
  tags: string[]
  deepLinkText: MultilingualString
  deepLinkUrl: MultilingualString
  modules: LearningModuleManagementModel[]
  rating: number
  ratingCount: number
  isTopRated: boolean
  isPopular: boolean
  isNew: boolean
}

export enum LearningModuleType {
  Markdown = 'markdown',
  Action = 'action',
  Quiz = 'quiz'
}

export interface LearningModuleManagementModel {
  id: string
  moduleType: LearningModuleType
  learningCourseId: string
  isPublished: boolean
  title: MultilingualString
  orderNumber: number
  estimatedCompletionTime: string
}

export interface LearningModuleMarkdownManagementModel extends LearningModuleManagementModel {
  content: MultilingualString
}

export interface LearningModuleActionManagementModel extends LearningModuleManagementModel {
  context: MultilingualString
  prompt: MultilingualString
  description: MultilingualString
  deepLinkText: MultilingualString
  deepLinkUrl: MultilingualString
}

export interface LearningModuleQuizManagementModel extends LearningModuleManagementModel {
  questions: LearningModuleQuizQuestionModel[]
}

export interface LearningCategoryCreateOrUpdateModel {
  orderNumber: number
  color: string
  backgroundColor: string
  icon: string
  title: MultilingualString
  description: MultilingualString
  tags: string[]
}

export interface LearningCourseCreateOrUpdateModel {
  learningCategoryId: string
  employerId: string | null
  title: MultilingualString
  description: MultilingualString
  imageUrl: string
  orderNumber: number
  tags: string[]
  deepLinkText: MultilingualString | null
  deepLinkUrl: MultilingualString | null
}

export interface LearningModuleCreateOrUpdateModel {
  moduleType: LearningModuleType
  title: MultilingualString
  orderNumber: number
  estimatedCompletionTime: string
}

export interface LearningModuleMarkdownCreateOrUpdateModel extends LearningModuleCreateOrUpdateModel {
  content: MultilingualString
}

export interface LearningModuleActionCreateOrUpdateModel extends LearningModuleCreateOrUpdateModel {
  context: MultilingualString
  prompt: MultilingualString
  description: MultilingualString
  deepLinkText: MultilingualString | null
  deepLinkUrl: MultilingualString | null
}

export interface LearningModuleQuizCreateOrUpdateModel extends LearningModuleCreateOrUpdateModel {
  questions: LearningModuleQuizQuestionModel[]
}

export enum LearningModuleQuizQuestionType {
  YesNo = 'yesNo',
  SingleChoice = 'singleChoice',
  MultipleChoice = 'multipleChoice'
}

export interface LearningModuleQuizQuestionModel {
  id: string | null
  published: boolean
  type: LearningModuleQuizQuestionType
  question: MultilingualString
  explanation: MultilingualString
  orderNumber: number
  alternatives: LearningModuleQuizQuestionAlternativeModel[]
}

export interface LearningModuleQuizQuestionAlternativeModel {
  id: string | null
  published: boolean
  answer: MultilingualString
  description: MultilingualString
  isCorrect: boolean,
  orderNumber: number
}

export interface EmployerPayRunDashboardModel {
  id: string
  paydayId: string
  employerDeductionPaymentBankAccountTransactionId: string | null
  employerDeductionPaymentBankAccountBatchTransactionItemId: string | null
  employerDeductionAmount: Money | null
  employerId: string
  customerId: string
  customerName: string
  paymentDateString: string
  paymentDate: string
  offCycle: boolean
  ignoreWhenMatchingBankAccountTransactions: boolean
}

export interface CommunicationOptOutsModel {
  userIds: string[]
  employeeIds: string[]
}


export interface SwishPayoutStatusResponse
{
  payoutIdentifier: string
  success: boolean
  responseCode: string
  errorMessage: string
  payout: SwishPayoutResponseModel | undefined
}

export interface SwishPayoutResponseModel
{
  payoutInstructionUuid: string
  payerPaymentReference: string
  payeeAlias: string
  payeeSsn: string
  amount: number
  currency: string
  message: string
  status: string
  payoutType: string
  dateCreated: string
  datePaid: string
  errorCode: string
  errorMessage: string
  additionalInformation: string
}

export interface CreditScoreOverviewItemModel {
  companyId: string
  companyRegistrationNumber: string
  creditScore: number | null
  creditRiskClassification: CreditRiskClassification
  probabilityOfDefaultPercent: number | null
  reportDate: string
}

export interface CreditUsageOverviewItemModel {
  customerId: string
  name: string
  companyRegistrationNumber: string
  approvedCreditLimit: Money
  availableCreditLimit: Money
  currentCreditUsage: Money
  recentMaxCreditUsage: Money
}

export interface CreditorFunding {
  creditorId: string
  creditorName: string
  type: string
  creditUsage: Money | null
  postfundedAmount: Money | null
  creditUsageLimit: Money | null
  remainingCreditSpace: Money | null
}
